import {
    ApolloClient,
    ApolloLink,
    createHttpLink,
    InMemoryCache,
    split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import TimeoutLink from 'apollo-link-timeout';
import { sha256 } from 'crypto-hash';
import { createClient } from 'graphql-ws';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import { store } from '../app/store';
import {
    getAltTemporaryToken,
    getTemporaryToken,
    refreshAccessToken,
} from './axios';
import { SecureStorageKeys } from './enums';
import router from './router';
import { logout, saveData, saveNetworkError } from './webview/messages';

export async function generateHeaders(includeLocation = false): Promise<{
    [key: string]: string | null | undefined;
}> {
    const storeState = store.getState();
    const headers: {
        [key: string]: string | null | undefined;
    } = {
        'x-app-version': `MA ${storeState.app.version}`,
        'x-event-id': uuidv4(),
        'x-device-type': storeState.app.deviceType,
        'x-base-os': storeState.app.platformOs,
        'x-net-info': storeState.app.netInfo,
        'x-phone-carrier': storeState.app.carrier,
        'x-device-invoked-at': new Date().toISOString(),
        'x-device-guid': storeState.app.deviceGuid,
        'x-webview-version': process.env.REACT_APP_GIT_COMMIT,
    };
    return Promise.race([
        new Promise<{
            [key: string]: string | null | undefined;
        }>((resolve) => {
            if (includeLocation) {
                headers['x-geolocation'] = JSON.stringify({
                    latitude: store.getState().app.latitude,
                    longitude: store.getState().app.longitude,
                });
            }
            resolve(headers);
        }),
        new Promise<{
            [key: string]: string | null | undefined;
        }>((resolve) => setTimeout(() => resolve(headers), 1000)),
    ]);
}

const retryLink = new RetryLink({
    attempts: {
        max: Infinity,
        retryIf: (error, operation) =>
            !!error &&
            ['GetUserCurrentTransaction'].includes(operation.operationName),
    },
    delay: {
        max: 2000,
        jitter: true,
    },
});

const timeoutLink = new ApolloLink((operation, forward) => {
    const operationName = operation.operationName;

    // Define timeout mappings for different operations
    const timeoutMap: { [key: string]: number } = {
        // Quick queries (5 seconds)
        GetUserCurrentTransaction: 5000,
        GetStationPricing: 5000,
        GetUserRecentStations: 5000,

        // Default queries (10 seconds)
        GetUserPayment: 10000,
        GetStripeTransaction: 10000,
        GetStripeTransactions: 10000,

        // Heavy queries (30 seconds)
        RemoteStart: 5000,
        RemoteStop: 30000,
        AddPaymentMethod: 30000,
    };

    const timeout = timeoutMap[operationName] || 10000; // Default to 10 seconds

    // Create a timeout link for this specific operation
    const operationTimeoutLink = new TimeoutLink(timeout);

    return operationTimeoutLink.request(operation, forward);
});
const errorLink = onError(({ networkError, operation }) => {
    if (networkError) {
        // alert(operation.variables.);
        if (
            operation.operationName === 'RemoteStart' ||
            operation.operationName === 'Stop'
        ) {
            const desiredChargieId = operation.variables.qrCode;
            const now = new Date();
            saveNetworkError(
                operation.operationName,
                desiredChargieId,
                now.toString()
            );
        }
        console.log(`[Network error]: ${networkError}`);
    }
});

const httpLink = createHttpLink({
    uri: process.env.REACT_APP_API_ENDPOINT,
});

// CRA leaves unset REACT_APP_* as undefined; HttpLink then defaults to
// relative "/graphql" (e.g. http://localhost:8083/graphql).
const altApiEndpoint = (process.env.REACT_APP_ALT_API_ENDPOINT || '')
    .trim()
    .replace(/^["']|["']$/g, '');

const altHttpLink = createHttpLink({
    uri: altApiEndpoint || undefined,
});

// Cross-realm auth: fetch an anonymous token from the alt auth host.
// Do not reuse/refresh the primary-realm session against the alt endpoint.
const altAuthLink = setContext(async (_operation, { headers }) => {
    let token = '';
    try {
        const result = await getAltTemporaryToken();
        token = result.data.access_token;
    } catch (e) {
        console.error('Failed to get alt auth token', e);
    }
    const metadataHeaders = await generateHeaders();
    return {
        headers: {
            ...metadataHeaders,
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
        },
    };
});

const persistedQueryLink = createPersistedQueryLink({
    sha256,
});

const logoutLink = onError(({ graphQLErrors }) => {
    if (
        graphQLErrors?.find(
            (error) => error.extensions?.code === 'UNAUTHENTICATED'
        )
    ) {
        router.navigate('/login', { replace: true });
    }
});

// Operations that are allowed for logged-out/anonymous users
// All other operations require authentication
const loggedOutAllowedOperations = [
    // Guest operations
    'GuestRemoteStart',
    'ContactSupport',
    // Public station/property info
    'GetStationPricing',
    'GetOtcStationDetails',
    'GetProperty',
    'GetMap',
    'GetSessionFee',
    // Authentication/signup
    'RegisterUser',
    'GetEmailDomainRealm',
    'SendLostPasswordEmail',
    'VerifyResetToken',
    'ResetPassword',
    'CreatePassword',
    // App/system
    'RequireForceUpdate',
    // Public review operations (if any)
    'RecordAppReview',
    'RecordAppAnswer',
];

// Block operations that require authentication for logged-out/anonymous users
const loggedInCheckLink = new ApolloLink((operation, forward) => {
    const operationName = operation.operationName;
    const refreshToken = store.getState().app.refreshToken;
    const accessToken = store.getState().app.accessToken;
    let loggedIn = true;

    // If operation is not in the whitelist, it requires authentication
    if (operationName && !loggedOutAllowedOperations.includes(operationName)) {
        // If no refresh token, user is logged out - block the operation
        if (!refreshToken) {
            loggedIn = false;
        } else if (accessToken) {
            try {
                const decoded: JwtPayload = jwtDecode<JwtPayload>(accessToken);
                if (decoded.sub === '00000000-0000-0000-0000-000000000000') {
                    loggedIn = false;
                }
            } catch (e) {
                // If we can't decode the token, allow it through (will fail on server)
            }
        }
    }

    if (!loggedIn) {
        throw new Error(`Operation ${operationName} requires authentication`);
    }

    return forward(operation);
});

const authLink = setContext(async (operation, { headers }) => {
    let token = store.getState().app.accessToken;
    try {
        if (token) {
            const decoded: JwtPayload = jwtDecode<JwtPayload>(token);
            const refreshToken = store.getState().app.refreshToken;
            if (
                decoded &&
                decoded.exp &&
                new Date(decoded.exp * 1000) < new Date()
            ) {
                // Expired JWT, refresh here
                try {
                    let result;
                    // If no user or no refresh token, get a new anonymous token
                    if (
                        decoded.sub ===
                            '00000000-0000-0000-0000-000000000000' ||
                        !refreshToken
                    ) {
                        result = await getTemporaryToken();
                    } else {
                        result = await refreshAccessToken(
                            token,
                            refreshToken,
                            await generateHeaders()
                        );
                        saveData(
                            SecureStorageKeys.ACCESS_TOKEN,
                            result.data.access_token
                        );
                    }
                    token = result.data.access_token;
                } catch {
                    // Refresh token expired
                    logout();
                    router.navigate('/login', { replace: true });
                }
            }
        } else {
            const result = await getTemporaryToken();
            token = result.data.access_token;
        }
    } finally {
        const includeLocation = [
            'GuestRemoteStart',
            'RemoteStart',
            'RemoteStop',
            'RegisterUser',
            'GetProperty',
        ];
        const metadataHeaders: {
            [key: string]: string | null | undefined;
        } = await generateHeaders(
            !!operation.operationName &&
                includeLocation.includes(operation.operationName)
        );
        return {
            headers: {
                ...metadataHeaders,
                ...headers,
                authorization: token ? `Bearer ${token}` : '',
            },
        };
    }
});

const wsLink = new GraphQLWsLink(
    createClient({
        url: `ws${
            process.env.REACT_APP_API_ENDPOINT?.includes('https://') ? 's' : ''
        }://${process.env.REACT_APP_API_ENDPOINT?.replace(
            /https?:\/\//,
            ''
        )}}/`,
        connectionParams: () => {
            let token = store.getState().app.refreshToken;
            return {
                Authorization: token ? `Bearer ${token}` : '',
            };
        },
    })
);

const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
        );
    },
    wsLink,
    persistedQueryLink.concat(httpLink)
);

const client = new ApolloClient({
    // different Queries hit the same 'station' field (GetStation, GetStationLogs, GetStationPricingHistory,...)
    // those clash and prevent Apollo from caching the response, this solves that issue
    // https://www.apollographql.com/docs/react/caching/advanced-topics/
    // PS also added the same for 'user'
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    station: {
                        read(_, { args, toReference }) {
                            return toReference({
                                __typename: 'Station',
                                id: args?.filter.id,
                            });
                        },
                    },
                    user: {
                        read(_, { args, toReference }) {
                            return toReference({
                                __typename: 'User',
                                id: args?.filter.id,
                            });
                        },
                    },
                },
            },
        },
    }),
    link: authLink
        .concat(loggedInCheckLink)
        .concat(retryLink)
        .concat(logoutLink)
        .concat(errorLink)
        .concat(timeoutLink)
        .concat(logoutLink)
        .concat(splitLink),
    defaultOptions: {
        query: {
            fetchPolicy: 'network-only',
        },
    },
});

// Separate client for cross-realm lookups (e.g. station exists in another realm).
// Uses REACT_APP_ALT_AUTH_ENDPOINT for Bearer tokens and REACT_APP_ALT_API_ENDPOINT for GraphQL.
export const altClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: altAuthLink.concat(altHttpLink),
    defaultOptions: {
        query: {
            fetchPolicy: 'no-cache',
        },
    },
});

export const getAltApiEndpoint = () => altApiEndpoint;

export default client;
